import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Square, Wand2 } from "lucide-react";
import { ClauseRedlineMark } from "@/components/common/ClauseRedlineMark";
import { streamPost, MODEL_LABEL } from "@/lib/sse";

const QUICK = ["Tighten the language and fix structure", "Strengthen the GROUNDS with the pleaded facts", "Add a limitation / delay explanation paragraph", "Make the PRAYER more specific", "Translate key paragraphs into Telugu (bilingual)"];
const SECTIONS = [["FACTS", "Facts"], ["GROUNDS", "Grounds"], ["PRAYER", "Prayer"], ["INTERIM_PRAYER", "Interim Prayer"], ["SYNOPSIS", "Synopsis"], ["LIST_OF_DATES", "List of Dates"], ["VERIFICATION", "Verification"], ["QUESTIONS_OF_LAW", "Questions of Law"]];

// Claude drafting assistant. Output is a proposal shown to the advocate and routed to a target section (B1/B2).
export function DraftAssistant({ draft, matterId, body, onStream, onDone }) {
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState(null);
  const [runMode, setRunMode] = useState(null);
  const abortRef = useRef(null);

  const run = async (mode, instr, section) => {
    setBusy(true); setModel(null); setRunMode(mode);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    let acc = "";
    onStream("");
    try {
      await streamPost("/ai/draft", { matterId, draftType: draft.type, title: draft.title, instruction: instr || instruction, currentText: body, mode, section }, {
        signal: ctrl.signal, onMeta: (ev) => setModel(ev.model), onDelta: (d) => { acc += d; onStream(acc); },
        onError: (e) => toast.error(e),
      });
      if (acc) { onDone(acc, section || (mode === "generate" ? "FACTS" : null)); toast.success(section ? `${section.replaceAll("_", " ")} proposal ready — choose where to insert.` : "Proposal ready — review every [verify] placeholder."); }
    } catch (e) { if (e.name !== "AbortError") toast.error("Connection lost."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="draft-assistant">
      <div className="flex items-center gap-1 text-xs font-semibold text-ink"><Sparkles className="h-3 w-3 text-copper" /> Drafting Assistant {model && <span className="ml-auto rounded-full bg-copper-light px-1.5 text-[10px] font-normal text-copper">{MODEL_LABEL[model] || model}</span>}</div>
      <Textarea rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Instruction — e.g. 'Ground on breach of CEA safety clearances; rely on the pleaded facts only.'" className="text-xs" data-testid="draft-instruction" />
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">Generate for section</div>
      <div className="flex flex-wrap gap-1">{SECTIONS.map(([k, l]) => <button key={k} disabled={busy} onClick={() => run("generate", null, k)} className="rounded-full border border-copper/40 px-2 py-0.5 text-[11px] text-copper hover:bg-copper-light disabled:opacity-40" data-testid={`draft-gen-${k}`}>{l}</button>)}</div>
      <div className="flex gap-1.5">
        {busy ? <Button size="sm" variant="outline" onClick={() => abortRef.current?.abort()} data-testid="draft-stop"><Square className="mr-1 h-3 w-3" /> Stop</Button> : (
          <>
            <Button size="sm" onClick={() => run("generate")} data-testid="draft-generate"><Wand2 className="mr-1 h-3 w-3" /> Full draft from facts</Button>
            <Button size="sm" variant="outline" onClick={() => run("refine")} disabled={!body?.trim()} data-testid="draft-refine">Refine draft</Button>
          </>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">Quick refinements</div>
      <div className="flex flex-wrap gap-1">{QUICK.map((q) => <button key={q} disabled={busy || !body?.trim()} onClick={() => run("refine", q)} className="rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-2 hover:border-copper/40 disabled:opacity-40" data-testid="draft-quick">{q}</button>)}</div>
      {busy && runMode === "refine" && (
        <div className="my-1 rounded-lg border border-line bg-surface p-2" data-testid="clause-redline-animation">
          <ClauseRedlineMark />
        </div>
      )}
      <div className="rounded bg-warning-bg px-2 py-1 text-[10px] text-warning">Uses matter facts only; unknowns appear as [verify: …]. Review before filing.</div>
    </div>
  );
}
